import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { trialService } from '../../services/trialService';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const Profile = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [trialStatus, setTrialStatus] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    fullName: '',
    avatarUrl: ''
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        fullName: userProfile?.full_name || '',
        avatarUrl: userProfile?.avatar_url || ''
      });
    }
  }, [userProfile]);

  // Load trial status
  useEffect(() => {
    if (user?.id) {
      loadTrialStatus();
    }
  }, [user]);

  const loadTrialStatus = async () => {
    if (!user?.id) return;
    
    setLoadingTrial(true);
    try {
      const status = await trialService?.checkTrialStatus(user?.id);
      setTrialStatus(status);
    } catch (error) {
      console.error('Failed to load trial status:', error);
    } finally {
      setLoadingTrial(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await updateProfile({
        full_name: formData?.fullName,
        avatar_url: formData?.avatarUrl
      });

      if (error) {
        setMessage({ type: 'error', text: error?.message });
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setEditing(false);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: userProfile?.full_name || '',
      avatarUrl: userProfile?.avatar_url || ''
    });
    setEditing(false);
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Profile Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your account information and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trial Status Card */}
          {!loadingTrial && trialStatus && (
            <div className="lg:col-span-3">
              {trialStatus?.isTrialActive ? (
                <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm">
                        <Icon name="Gift" size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Free Trial Active</h3>
                        <p className="text-sm text-white/90 mt-1">
                          {trialStatus?.daysRemaining} {trialStatus?.daysRemaining === 1 ? 'day' : 'days'} remaining until billing starts
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/80">Trial ends on</p>
                      <p className="text-sm font-semibold">
                        {trialStatus?.trialEndDate ? new Date(trialStatus.trialEndDate)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-xs text-white/90">
                      💳 You will be charged after the trial period ends. Cancel anytime from Billing Settings.
                    </p>
                  </div>
                </div>
              ) : trialStatus?.hasConverted ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center gap-3">
                  <Icon name="CheckCircle" size={20} className="text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">Active Subscription</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">Your trial has been converted to a paid subscription</p>
                  </div>
                </div>
              ) : trialStatus?.isExpired ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center gap-3">
                  <Icon name="AlertTriangle" size={20} className="text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Trial Expired</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Please update your billing information to continue</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Profile Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-blue-500 h-32"></div>
          
          <div className="px-8 pb-8">
            {/* Avatar */}
            <div className="-mt-16 mb-6">
              <div className="h-32 w-32 rounded-full border-4 border-white dark:border-slate-800 bg-gradient-to-br from-emerald-400 to-blue-400 flex items-center justify-center shadow-xl">
                {userProfile?.avatar_url ? (
                  <img
                    src={userProfile?.avatar_url}
                    alt={userProfile?.full_name}
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-bold text-white">
                    {userProfile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
            </div>

            {/* Messages */}
            {message?.text && (
              <div className={`mb-6 rounded-lg p-4 flex items-start gap-3 ${
                message?.type === 'success' ?'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <Icon
                  name={message?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                  size={20}
                  className={`flex-shrink-0 mt-0.5 ${
                    message?.type === 'success' ?'text-green-600 dark:text-green-400' :'text-red-600 dark:text-red-400'
                  }`}
                />
                <p className={`text-sm ${
                  message?.type === 'success' ?'text-green-800 dark:text-green-200' :'text-red-800 dark:text-red-200'
                }`}>
                  {message?.text}
                </p>
              </div>
            )}

            {/* Profile Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name
                  </label>
                  {editing ? (
                    <Input
                      name="fullName"
                      type="text"
                      value={formData?.fullName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      disabled={loading}
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white py-2">{userProfile?.full_name || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <p className="text-gray-900 dark:text-white py-2">{user?.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <p className="text-gray-900 dark:text-white py-2 capitalize">{userProfile?.role || 'User'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Account Status
                  </label>
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    userProfile?.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    <Icon name={userProfile?.is_active ? 'CheckCircle' : 'XCircle'} size={14} />
                    {userProfile?.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {editing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Avatar URL
                  </label>
                  <Input
                    name="avatarUrl"
                    type="url"
                    value={formData?.avatarUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/avatar.jpg"
                    disabled={loading}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                {editing ? (
                  <>
                    <Button
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <Icon name="Loader2" size={18} className="animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Icon name="Save" size={18} />
                          Save Changes
                        </span>
                      )}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCancel}
                      disabled={loading}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    onClick={() => setEditing(true)}
                  >
                    <span className="flex items-center gap-2">
                      <Icon name="Edit" size={18} />
                      Edit Profile
                    </span>
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;